import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';

const ComparisonChart = ({
  title,
  data,
  type = 'bar', // 'bar', 'pie', 'donut'
  dataKey = 'value',
  nameKey = 'name',
  height = 300,
  colors = ['#003366', '#D4AF37', '#4A90E2', '#50C878', '#FF6B6B', '#FFA500', '#9B59B6', '#34495E'],
  stacked = false,
  horizontal = false,
  loading = false,
  yAxisFormat = 'number'
}) => {
  const formatValue = (value) => {
    switch (yAxisFormat) {
      case 'currency':
        return `$${value.toLocaleString()}`;
      case 'percent':
        return `${value}%`;
      case 'number':
      default:
        return value.toLocaleString();
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            boxShadow: 2
          }}
        >
          <Typography variant="body2" fontWeight="bold" mb={0.5}>
            {payload[0].name}
          </Typography>
          {payload.map((entry, index) => (
            <Box key={index} display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  borderRadius: '50%'
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {entry.dataKey || 'Value'}:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatValue(entry.value)}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null; // Don't show labels for small slices

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize="12"
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>{title}</Typography>
          <Box display="flex" alignItems="center" justifyContent="center" height={height}>
            <Typography color="text.secondary">Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const renderBarChart = () => {
    const dataKeys = Array.isArray(dataKey) ? dataKey : [dataKey];

    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout={horizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          {horizontal ? (
            <>
              <XAxis type="number" tickFormatter={formatValue} tick={{ fontSize: 12 }} stroke="#666" />
              <YAxis type="category" dataKey={nameKey} tick={{ fontSize: 12 }} stroke="#666" />
            </>
          ) : (
            <>
              <XAxis dataKey={nameKey} tick={{ fontSize: 12 }} stroke="#666" />
              <YAxis tickFormatter={formatValue} tick={{ fontSize: 12 }} stroke="#666" />
            </>
          )}
          <Tooltip content={<CustomTooltip />} />
          <Legend iconType="circle" />
          {dataKeys.map((key, index) => (
            <Bar
              key={key.dataKey || key}
              dataKey={key.dataKey || key}
              name={key.name || key}
              fill={colors[index % colors.length]}
              radius={[8, 8, 0, 0]}
              stackId={stacked ? 'stack' : undefined}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderPieChart = () => {
    const innerRadius = type === 'donut' ? '60%' : 0;

    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={<CustomPieLabel />}
            outerRadius="80%"
            innerRadius={innerRadius}
            fill="#8884d8"
            dataKey={dataKey}
            nameKey={nameKey}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={36}
            iconType="circle"
          />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          {title}
        </Typography>
        {type === 'pie' || type === 'donut' ? renderPieChart() : renderBarChart()}
      </CardContent>
    </Card>
  );
};

export default ComparisonChart;
